import { useMemo, useState, type ReactNode } from "react";
import { api, useLoad } from "../api";
import type { ScannerRow, Summary } from "../types";
import { CATEGORY_LABEL, CATEGORY_ORDER, fmt, zoneOf } from "../lib/format";
import { linkHandler } from "../lib/router";
import { PositionRail } from "../components/PositionRail";
import { Sparkline } from "../components/Sparkline";
import { Labeled } from "../components/Hint";
import { HINTS } from "../lib/hints";
import { auth } from "../lib/auth";

type Filter = "all" | "bull" | "bear" | "setup";

export function ScannerPage({ demo }: { demo: boolean }) {
  const { data, error, loading } = useLoad((s) => (demo ? api.demoScanner(s) : api.scanner(s)), [demo]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const rows = data ?? [];
  const withData = rows.filter((r) => r.hasData && r.summary);
  const counts = useMemo(
    () => ({
      bull: withData.filter((r) => zoneOf(r.summary!.smartCotShort) === "bull").length,
      bear: withData.filter((r) => zoneOf(r.summary!.smartCotShort) === "bear").length,
      setup: withData.filter((r) => r.summary!.setup !== 0 || Math.abs(r.summary!.score) >= 3).length,
    }),
    [withData],
  );
  const lastDate = withData.map((r) => r.summary!.lastDate ?? "").sort().at(-1) ?? null;

  const visible = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !r.code.toLowerCase().includes(q)) return false;
    if (filter === "all") return true;
    if (!r.summary) return false;
    if (filter === "setup") return r.summary.setup !== 0 || Math.abs(r.summary.score) >= 3;
    return zoneOf(r.summary.smartCotShort) === filter;
  });
  const groups = CATEGORY_ORDER.map((c) => ({ c, items: visible.filter((r) => r.category === c) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <main className="page scanner">
      <section className="hero">
        <h1>Где сейчас умные деньги</h1>
        <p className="lede">
          Позиции юрлиц и физлиц на срочном рынке Мосбиржи. Точка на шкале — индекс COT за 26 недель: насколько
          позиция группы близка к максимуму (100) или минимуму (0) своего полугодового диапазона.
        </p>
        {demo && (
          <div className="demo-banner">
            <span>
              <b>Демо-версия.</b> Открыты {auth.demoMarkets().join(", ") || "несколько инструментов"}. Остальные{" "}
              {Math.max(0, rows.length - withData.length)} инструментов, фильтры и сигналы — после входа.
            </span>
            <a className="btn btn-primary" href="/login" onClick={linkHandler("/login")}>
              Войти
            </a>
          </div>
        )}
        <p className="asof">
          {lastDate ? (
            <>
              <span className="status-dot" aria-hidden="true" />
              Позиции на <b>{fmt.date(lastDate)}</b>. Цены — по закрытию прошлого торгового дня
            </>
          ) : loading ? (
            "Загружаем данные…"
          ) : (
            ""
          )}
        </p>
        <nav className="quick-links" aria-label="Другие разделы">
          <a href="/ru/journal" onClick={linkHandler("/ru/journal")}>
            Журнал сигналов всех рынков <span aria-hidden="true">→</span>
          </a>
          <a href="/ru/backtest" onClick={linkHandler("/ru/backtest")}>
            Бэктест по всем рынкам <span aria-hidden="true">→</span>
          </a>
          <a href="/settings" onClick={linkHandler("/settings")}>
            Настройки параметров <span aria-hidden="true">→</span>
          </a>
        </nav>
      </section>

      <div className="toolbar filter-panel" role="toolbar" aria-label="Фильтры">
        <div className="segmented filters">
          <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
            Все <span className="count">{rows.length}</span>
          </FilterButton>
          <FilterButton active={filter === "bull"} onClick={() => setFilter("bull")}>
            Юрлица у максимума <span className="count">{counts.bull}</span>
          </FilterButton>
          <FilterButton active={filter === "bear"} onClick={() => setFilter("bear")}>
            Юрлица у минимума <span className="count">{counts.bear}</span>
          </FilterButton>
          <FilterButton active={filter === "setup"} onClick={() => setFilter("setup")}>
            Есть сетап <span className="count">{counts.setup}</span>
          </FilterButton>
        </div>
        <input
          className="search"
          type="search"
          placeholder="Найти инструмент"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Найти инструмент"
        />
      </div>

      {loading && !data && (
        <div className="notice notice-loading" role="status">
          <span className="spinner" aria-hidden="true" /> Считаем позиции по всем рынкам… Сразу после запуска сервера
          или ночной загрузки это занимает до минуты, дальше страница открывается мгновенно.
        </div>
      )}
      {error && (
        <div className="notice notice-error">
          Сервер не отвечает: {error}. Запустите бэкенд (см. README, раздел «Запуск») и обновите страницу.
        </div>
      )}
      {!error && !loading && rows.length > 0 && withData.length === 0 && (
        <div className="notice">
          Справочник рынков загружен, но истории позиций ещё нет. Сервер загружает её сам при старте; вручную —{" "}
          <code>POST /api/admin/ru/sync</code>. Ход загрузки — <code>GET /api/admin/ingest-log</code>.
        </div>
      )}

      <div className="legend-line" aria-label="Обозначения шкалы">
        <span className="legend-title">Шкала:</span>
        <span>
          <i className="rail-dot-key smart" aria-hidden="true" /> Юрлица (умные деньги)
        </span>
        <span>
          <i className="rail-dot-key crowd" aria-hidden="true" /> Физлица (толпа)
        </span>
        <span>
          <i className="zone-key bear" aria-hidden="true" />
          0–20 — у минимума
        </span>
        <span>
          <i className="zone-key bull" aria-hidden="true" />
          80–100 — у максимума
        </span>
      </div>

      {groups.map((g) => (
        <section key={g.c} className="cat">
          <h2>
            {CATEGORY_LABEL[g.c] ?? g.c} <span className="h-count">{g.items.length}</span>
          </h2>
          <div className="table" role="table" aria-label={CATEGORY_LABEL[g.c] ?? g.c}>
            <div className="trow thead" role="row">
              <span role="columnheader">Инструмент</span>
              <span role="columnheader">
                <Labeled hint={HINTS.rail}>Позиционная шкала</Labeled>
              </span>
              <span role="columnheader" className="num">
                <Labeled hint={HINTS.cot} align="end">COT 26 н.</Labeled>
              </span>
              <span role="columnheader" className="num">
                <Labeled hint={HINTS.cotLong} align="end">COT 3 года</Labeled>
              </span>
              <span role="columnheader" className="num">
                <Labeled hint={HINTS.willco} align="end">WILLCO</Labeled>
              </span>
              <span role="columnheader" className="num">
                <Labeled hint={HINTS.oi} align="end">Интерес</Labeled>
              </span>
              <span role="columnheader">
                <Labeled hint={HINTS.trend}>Тренд M19</Labeled>
              </span>
              <span role="columnheader">
                <Labeled hint={HINTS.setup} align="end">Сетап</Labeled>
              </span>
              <span role="columnheader">
                <Labeled hint={HINTS.spark} align="end">Год</Labeled>
              </span>
            </div>
            {g.items.map((r) => (
              <Row key={r.code} r={r} />
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}

function Row({ r }: { r: ScannerRow }) {
  if (r.locked) {
    const to = `/login?next=${encodeURIComponent(`/ru/${r.code}`)}`;
    return (
      <a className="trow locked" role="row" href={to} onClick={linkHandler(to)}>
        <span className="inst" role="cell">
          <b>{r.name}</b>
          <small>{r.code}</small>
        </span>
        <span role="cell" className="lock-cell">
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          Данные после входа
        </span>
      </a>
    );
  }
  const s = r.summary;
  const href = `/ru/${encodeURIComponent(r.code)}`;
  if (s && s.smartCotShort == null) {
    return (
      <a className="trow nodata" role="row" href={href} onClick={linkHandler(href)}>
        <span className="inst" role="cell">
          <b>{r.name}</b>
          <small>{r.code}</small>
        </span>
        <span role="cell" className="lock-cell">
          Недостаточно данных
        </span>
      </a>
    );
  }
  return (
    <a className={`trow ${s ? "" : "nodata"}`} role="row" href={href} onClick={linkHandler(href)}>
      <span className="inst" role="cell">
        <span className="inst-top">
          <b>{r.name}</b>
          <span className="ticker">{r.code}</span>
        </span>
        <small>
          {r.perpetual ? "вечный · " : ""}
          {s?.lastDate ? `отчёт ${fmt.shortDate(s.lastDate)}` : ""}
          {s?.partial && (
            <span className="partial-mark" title="Бар не закрыт: итоговый снимок позиций дня — 23:50, значения предварительные">
              предв.
            </span>
          )}
        </small>
      </span>
      <span role="cell" className="rail-cell">
        {s ? <PositionRail smart={s.smartCotShort} crowd={s.publicCotShort} /> : <em className="muted">нет данных</em>}
      </span>
      <span role="cell" className={`num zone-${zoneOf(s?.smartCotShort ?? null)}`}>
        {fmt.idx(s?.smartCotShort ?? null)}
      </span>
      <span role="cell" className={`num zone-${zoneOf(s?.smartCotLong ?? null)}`}>
        {fmt.idx(s?.smartCotLong ?? null)}
      </span>
      <span role="cell" className={`num zone-${zoneOf(s?.smartWillco ?? null, 75, 25)}`}>
        {fmt.idx(s?.smartWillco ?? null)}
        {r.sectorSignal !== 0 && (
          <span
            className={`sector-badge ${r.sectorSignal > 0 ? "up" : "down"}`}
            title={`Секторный сигнал (гл. 10): WILLCO ${r.sectorSignal > 0 ? "≥ 75" : "≤ 25"} у ${fmt.int(
              r.sectorSignal > 0 ? r.sectorBullPct : r.sectorBearPct,
            )}% рынков категории`}
          >
            сектор
          </span>
        )}
      </span>
      <span role="cell" className={`num zone-${oiZone(s?.oiStoch ?? null)}`}>
        {fmt.idx(s?.oiStoch ?? null)}
      </span>
      <span role="cell" title={s ? `Тренд сетапа M19; по 52-недельной средней: ${s.trend > 0 ? "рост" : s.trend < 0 ? "падение" : "нет данных"}` : undefined}>
        {s ? <TrendMark t={s.trendUnified} /> : null}
      </span>
      <span role="cell">{s ? <Score score={s.score} setup={s.setup} summary={s} /> : null}</span>
      <span role="cell" className="spark-cell">
        {s ? <Sparkline values={s.spark} /> : null}
      </span>
    </a>
  );
}

/** Индекс OI: низкий (≤ 20) — зона покупок, «в бассейне никого»; высокий (≥ 80) — толпа в рынке (гл. 8). */
function oiZone(v: number | null): string {
  if (v == null) return "none";
  return v <= 20 ? "bull" : v >= 80 ? "bear" : "mid";
}

export function TrendMark({ t }: { t: number }) {
  if (t === 2)
    return (
      <span className="trend flat" title="Плоская средняя: правила гл. 15 разрешают обе стороны — и покупки, и продажи">
        <span aria-hidden="true">→</span> флэт
      </span>
    );
  if (t > 0)
    return (
      <span className="trend up">
        <span aria-hidden="true">↗</span> рост
      </span>
    );
  if (t < 0)
    return (
      <span className="trend down">
        <span aria-hidden="true">↘</span> падение
      </span>
    );
  return (
    <span className="trend none">
      <span aria-hidden="true">→</span> нет данных
    </span>
  );
}

/**
 * Состав балла — та же логика, что RuAnalytics.scores на сервере (ТЗ M19): Score^Buy = 3·Core^Buy + [сезонность вверх] +
 * [толпа против], Score^Sell — зеркально; показывается больший из двух (равные — 0).
 */
function scoreParts(s: Summary) {
  const d = s.setup !== 0 ? s.setup : s.score > 0 ? 1 : -1;
  const buy = d > 0;
  const crowd = s.crowdAccountsChange;
  return [
    {
      ok: s.setup === d,
      pts: 3,
      text: "Ядро: тренд + юрлица + интерес",
      why: `тренд сетапа ${s.trendUnified === 2 ? "плоский (обе стороны)" : s.trendUnified > 0 ? "вверх" : s.trendUnified < 0 ? "вниз" : "не определён"}; COT ${fmt.idx(
        s.smartCotShort,
      )} (${buy ? "≥ 80" : "≤ 20"}) или WILLCO ${fmt.idx(s.smartWillco)} (${buy ? "≥ 75" : "≤ 25"}); индекс OI ${fmt.idx(
        s.oiStoch,
      )} (${buy ? "≤ 20" : "≥ 80"})`,
    },
    {
      ok: s.seasonalBias === d,
      pts: 1,
      text: `Сезонность ${buy ? "вверх" : "вниз"}`,
      why:
        s.seasonalBias == null
          ? "нет истории цен"
          : `на 6 недель вперёд: ${s.seasonalBias > 0 ? "рост" : s.seasonalBias < 0 ? "падение" : "без склонности"}`,
    },
    {
      ok: crowd != null && (buy ? crowd < 0 : crowd > 0),
      pts: 1,
      text: buy ? "Физлица уходят из лонгов" : "Физлица набирают лонги",
      why: `счета физлиц (лонг − шорт) за 4 недели: ${crowd == null ? "—" : fmt.signed(crowd)}. COT физлиц не используется: на Мосбирже он равен 100 − COT юрлиц`,
    },
  ];
}

export function Score({ score, setup, summary }: { score: number; setup: number; summary?: Summary | null }) {
  if (score === 0) {
    return (
      <span className="score none" title={summary ? `Нет перевеса: балл покупки ${summary.scoreBuy}, продажи ${summary.scoreSell} (ядро сетапа и подтверждения)` : "Нет ядра сетапа и подтверждений"}>
        —
      </span>
    );
  }
  const dir = score > 0 ? "buy" : "sell";
  const n = Math.abs(score);
  const body = (
    <span className={`score ${dir} ${setup !== 0 ? "core" : ""}`}>
      <svg className="score-icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        {score > 0 ? (
          <path d="M2 11.5 L6.5 7 L9 9.5 L14 4.5 M10 4.5 H14 V8.5" />
        ) : (
          <path d="M2 4.5 L6.5 9 L9 6.5 L14 11.5 M10 11.5 H14 V7.5" />
        )}
      </svg>
      {score > 0 ? "Покупка" : "Продажа"}
      <span className="pips" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <i key={i} className={i < n ? "on" : ""} />
        ))}
      </span>
    </span>
  );
  if (!summary) return body;
  return (
    <span
      className="hint hint-end score-hint"
      tabIndex={0}
      role="button"
      aria-label={`Балл ${n} из 5`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {body}
      <span className="hint-pop" role="tooltip">
        <b className="hint-title">
          {score > 0 ? "Покупка" : "Продажа"}: {n} из 5
        </b>
        <ul className="checks">
          {scoreParts(summary).map((p) => (
            <li key={p.text} className={p.ok ? "ok" : "no"}>
              <span className="mark">{p.ok ? "✓" : "✗"}</span>
              <span>
                {p.text} <em>+{p.pts}</em>
                <small>{p.why}</small>
              </span>
            </li>
          ))}
        </ul>
      </span>
    </span>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={`filter-btn${active ? " active" : ""}`} aria-pressed={active} onClick={onClick}>
      {children}
    </button>
  );
}
